import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Sparkles, Wand2, ImageIcon, Eye, Flame, Clock } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [sort, setSort] = useState("trending");

  useEffect(() => {
    setItems(null);
    api.get(`/studio/public/gallery?sort=${sort}`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [sort]);

  return (
    <div className="min-h-screen bg-app relative pt-12 pb-28" data-testid="gallery-page">
      <Seo
        title="Explore Community Designs"
        description="Browse a gallery of AI-designed sci-fi apparel created by the AETHER community. Get inspired, then design your own custom t-shirt or hoodie."
        path="/explore"
      />
      <div className="absolute inset-0 hud-grid opacity-15" />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.4em] text-cyan/70">
              <Sparkles className="w-3.5 h-3.5" /> COMMUNITY VAULT
            </div>
            <h1 className="font-head text-4xl sm:text-5xl font-bold uppercase mt-2">
              Explore <span className="text-cyan text-glow-cyan">Designs</span>
            </h1>
            <p className="text-slate-400 mt-3 max-w-xl">Sci-fi artwork forged by AETHER creators. Tap any design to view it — or make your own.</p>
          </div>
          <Link to="/studio" data-testid="gallery-create-btn"
            className="btn-hud flex items-center gap-2 border border-cyan text-cyan px-5 py-2.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app">
            <Wand2 className="w-4 h-4" /> Design your own
          </Link>
        </div>

        <div className="flex gap-2 mb-6" data-testid="gallery-sort">
          {[["trending", "Trending", Flame], ["new", "Newest", Clock]].map(([val, label, Icon]) => (
            <button key={val} onClick={() => setSort(val)} data-testid={`gallery-sort-${val}`}
              className={`flex items-center gap-2 font-mono text-[10px] tracking-widest px-4 py-2 border transition-all ${sort === val ? "border-cyan text-cyan box-glow-cyan" : "border-white/15 text-slate-400 hover:border-cyan/40"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {items === null ? (
          <div className="text-center py-24 text-cyan/60 font-mono text-xs tracking-widest animate-pulse-glow">LOADING VAULT...</div>
        ) : items.length === 0 ? (
          <div className="glass clip-chamfer brackets p-14 text-center" data-testid="gallery-empty">
            <ImageIcon className="w-10 h-10 text-cyan/40 mx-auto mb-4" />
            <p className="text-slate-400">No shared designs yet. Be the first — create one in the Studio and hit Share.</p>
            <Link to="/studio" className="btn-hud inline-flex mt-6 border border-cyan/40 text-cyan px-6 py-2.5 text-sm hover:bg-cyan hover:text-app">Open Design Studio</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {items.map((it, i) => (
              <motion.div key={it.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: (i % 8) * 0.05 }}>
                <Link to={`/d/${it.id}`} data-testid={`gallery-item-${it.id}`}
                  className="group block glass border border-white/10 hover:border-cyan/50 transition-all overflow-hidden">
                  <div className="relative aspect-square bg-[#0a0f1c] overflow-hidden flex items-center justify-center"
                    style={{ backgroundImage: "linear-gradient(45deg,#11182a 25%,transparent 25%),linear-gradient(-45deg,#11182a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#11182a 75%),linear-gradient(-45deg,transparent 75%,#11182a 75%)", backgroundSize: "18px 18px", backgroundPosition: "0 0,0 9px,9px -9px,-9px 0px" }}>
                    {sort === "trending" && i < 3 && (
                      <span className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-amber text-app font-mono text-[8px] tracking-widest px-2 py-1" data-testid={`gallery-trending-badge-${it.id}`}>
                        <Flame className="w-3 h-3" /> TRENDING
                      </span>
                    )}
                    <img src={resolveImg(it.image_url)} alt={it.name || "AETHER community design"}
                      className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-3 flex items-center justify-between gap-2">
                    <div className="font-body text-sm text-slate-200 truncate group-hover:text-cyan transition-colors">{it.name}</div>
                    <div className="flex items-center gap-1 shrink-0 font-mono text-[10px] text-slate-500" title="views">
                      <Eye className="w-3 h-3" /> {it.views ?? 0}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
